import { useQuery } from "@tanstack/react-query";
import { companiesRepository } from "@/repositories/companies";

const MAIN_COMPANY_QUERY_KEY = ["main-company-id"] as const;

/**
 * Resolves the platform-wide "main company" id from the database
 * (companies.is_main = true) through the companies repository (the single
 * home for that call) — Nest-first via GET /companies/main, falling back to
 * the legacy get_main_company_id() Supabase RPC when Nest is unavailable or
 * its response fails schema validation.
 *
 * Replaces the legacy VITE_MAIN_COMPANY_ID env var fallback. Only one
 * company can be flagged main; super-admins switch it via set_main_company().
 */
export function useMainCompanyId() {
  const query = useQuery({
    queryKey: MAIN_COMPANY_QUERY_KEY,
    queryFn: () => companiesRepository.getMainCompanyId(),
    // No caching — always reflect the current DB value. Main company
    // changes are infrequent but must be picked up immediately.
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
  });

  return {
    mainCompanyId: query.data ?? null,
    isLoading: query.isLoading,
    error: query.error,
  };
}

export const mainCompanyQueryKey = MAIN_COMPANY_QUERY_KEY;
